import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowLeft, Play, FileText, HelpCircle, Clock, Users, BookOpen, Lock, CheckCircle, Loader2, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

export default function PublicCourseDetail() {
  const { courseId } = useParams();
  const subdomain = useStoreSubdomain();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', payment_method: 'qris' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [storeRes, courseRes] = await Promise.all([
          api.get(`/store/${subdomain}`),
          api.get(`/store/${subdomain}/courses/${courseId}`)
        ]);
        setStore(storeRes.data);
        setCourse(courseRes.data.course);
        setSections(courseRes.data.sections || []);
        setStats(courseRes.data.stats || {});
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [subdomain, courseId]);

  const handleEnroll = async () => {
    if (!form.customer_name || !form.customer_email) {
      toast.error('Nama dan email harus diisi');
      return;
    }
    setEnrolling(true);
    try {
      const res = await api.post(`/store/${subdomain}/courses/${courseId}/enroll`, form);
      if (res.data.payment_url) {
        toast.success('Silakan selesaikan pembayaran');
        window.open(res.data.payment_url, '_blank');
      } else {
        toast.success('Berhasil enroll! Silakan login untuk mengakses kursus.');
      }
      setShowEnrollDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal enroll');
    } finally {
      setEnrolling(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const primaryColor = store?.primary_color || '#2D6CDF';

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return <Play size={14} className="text-blue-500" />;
      case 'text': return <FileText size={14} className="text-emerald-500" />;
      case 'quiz': return <HelpCircle size={14} className="text-amber-500" />;
      default: return <FileText size={14} />;
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 size={32} className="animate-spin text-[#2D6CDF]" />
    </div>
  );

  if (!course) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <BookOpen size={48} className="mx-auto text-slate-300 mb-4" />
        <h1 className="text-xl font-bold text-slate-900">Kursus Tidak Ditemukan</h1>
        <Link to={`/store/${subdomain}`} className="text-[#2D6CDF] hover:underline mt-2 inline-block">Kembali ke Toko</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-course-detail">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={`/store/${subdomain}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft size={20} />
            <span className="font-medium">{store?.name}</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Badge className="mb-4" style={{ backgroundColor: primaryColor }}>{course.category || 'Kursus'}</Badge>
              <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>{course.title}</h1>
              <p className="text-slate-300 mb-6">{course.description}</p>
              <div className="flex items-center gap-6 text-sm text-slate-300">
                <span className="flex items-center gap-1"><Users size={16} /> {stats.enrollment_count || 0} siswa</span>
                <span className="flex items-center gap-1"><BookOpen size={16} /> {stats.total_lessons || 0} lesson</span>
                <span className="flex items-center gap-1"><Clock size={16} /> {stats.total_duration || 0} menit</span>
              </div>
              {course.instructor && (
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <GraduationCap size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Instructor</p>
                    <p className="font-medium">{course.instructor}</p>
                  </div>
                </div>
              )}
            </div>
            <div>
              <Card className="border-0 shadow-xl">
                <CardContent className="p-6">
                  {course.thumbnail && (
                    <img src={course.thumbnail} alt={course.title} className="w-full aspect-video object-cover rounded-lg mb-4" />
                  )}
                  <div className="text-center">
                    {course.price > 0 ? (
                      <p className="text-3xl font-bold text-slate-900 mb-1">Rp {fmt(course.price)}</p>
                    ) : (
                      <p className="text-2xl font-bold text-emerald-600 mb-1">GRATIS</p>
                    )}
                    <Button onClick={() => setShowEnrollDialog(true)} className="w-full mt-4" size="lg" style={{ backgroundColor: primaryColor }} data-testid="enroll-btn">
                      <GraduationCap size={18} className="mr-2" /> Daftar Sekarang
                    </Button>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <CheckCircle size={16} className="text-emerald-500" /> Akses selamanya
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <CheckCircle size={16} className="text-emerald-500" /> Sertifikat kelulusan
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <CheckCircle size={16} className="text-emerald-500" /> {stats.total_sections} section, {stats.total_lessons} lesson
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Kurikulum</h2>
            <Accordion type="multiple" defaultValue={sections.map(s => s.id)} className="space-y-3">
              {sections.map((section, sIdx) => (
                <AccordionItem key={section.id} value={section.id} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                  <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                    <div className="flex items-center gap-3 text-left">
                      <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600">{sIdx + 1}</span>
                      <div>
                        <h3 className="font-medium text-slate-900">{section.title}</h3>
                        <p className="text-xs text-slate-500">{section.lessons?.length || 0} lessons</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-2 mt-2">
                      {section.lessons?.map((lesson, lIdx) => (
                        <div key={lesson.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {getLessonIcon(lesson.type)}
                            <div>
                              <p className="text-sm font-medium text-slate-900">{lesson.title}</p>
                              <p className="text-xs text-slate-500">{lesson.duration_minutes} menit</p>
                            </div>
                          </div>
                          {lesson.is_preview ? (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">Preview</Badge>
                          ) : (
                            <Lock size={14} className="text-slate-400" />
                          )}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div>
            <Card className="border-slate-200 shadow-sm sticky top-20">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Yang Akan Dipelajari</h3>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    <span>Pemahaman konsep dasar hingga advanced</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    <span>Praktik langsung dengan contoh nyata</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    <span>Quiz interaktif untuk evaluasi</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-emerald-500 mt-0.5" />
                    <span>Sertifikat setelah menyelesaikan kursus</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Enroll Dialog */}
      <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Daftar Kursus</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium text-slate-900">{course.title}</p>
              <p className="text-2xl font-bold mt-1" style={{ color: primaryColor }}>
                {course.price > 0 ? `Rp ${fmt(course.price)}` : 'GRATIS'}
              </p>
            </div>
            <div>
              <Label>Nama Lengkap *</Label>
              <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Nama Anda" data-testid="enroll-name-input" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="email@example.com" data-testid="enroll-email-input" />
            </div>
            <div>
              <Label>No. Telepon</Label>
              <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="08123456789" />
            </div>
            <Button onClick={handleEnroll} className="w-full" style={{ backgroundColor: primaryColor }} disabled={enrolling} data-testid="confirm-enroll-btn">
              {enrolling ? <Loader2 size={16} className="animate-spin mr-2" /> : <GraduationCap size={16} className="mr-2" />}
              {enrolling ? 'Memproses...' : course.price > 0 ? 'Bayar & Daftar' : 'Daftar Gratis'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
